/* eslint-disable */
import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core';
import { versionComponent } from '../../global/inject-dependencies';

const players = {
  default: '5tEM6BSxSNd28ZZCQLweM4',
  loop: '-_VV2zpMELJzqDNgrB5TG9',
};

@Component({
  tag: 'ui-video',
  styleUrl: 'ui-video.scss',
  shadow: true,
})
export class UiVideo {
  @Element() el: HTMLElement;

  /**
   * A unique string used to identify and differentiate each video. The embed code for one video will always have a different video-id than the embed code for another video. This ID is also listed in the metadata accordion menu of the video in VideoManager Pro. You can also query a list of videos along with their IDs by using the "videos" endpoint in the movingimage REST API.
   */
  @Prop() videoId: string;

  /**
   * Text that appears beneath the video player, regarding its copyright information.
   */
  @Prop() copyrightText?: string;

  /**
   * When set to *true*, the video loops indefinitely. The default value is *false*.
   */
  @Prop() looped = false;

  /**
   * When set to *true*, the video has no sound. The default value is *false*.
   */
  @Prop() muted = false;

  /**
   * When set to *true*, the video has an opaque overlay when paused. The default value is *false*.
   */
  @Prop() overlay? = false;

  /**
   * Video source
   */
  @Prop() src: string;

  /**
   * Video text in case of something went wrong loading the video player
   */
  @Prop() errorText: string;

  /**
   * If set, the video won't exceed the height of the element above.
   * If you want to center the video, check out the flex-stuff in .superteaser--contained
   */
  @Prop() contain = false;

  @State() playerId: string;

  @State() htmlPath;

  @State() playing = false;

  @State() displayError = false;

  /**
   * Will calculate the aspectRatio. Only needed if @prop contain === true
   */
  @State() aspectRatio: number;

  /**
   * Reference to .ui-video-div. Only needed if @prop contain === true
   */
  @State() videoContainer: HTMLElement;

  /**
   * This observer checks if the video is being resized. Only needed if @prop contain === true
   */
  @State() observer: ResizeObserver;

  /**
   * Fired when the video is start playing
   */
  @Event() uiVideoPlay: EventEmitter;

  /**
   * Fired when pausing the video
   */
  @Event() uiVideoPause: EventEmitter;

  /**
   * Fired if the video throws any error
   */
  @Event() uiVideoError: EventEmitter;

  video: HTMLVideoElement;

  iframe: HTMLUiIframeElement;

  playingCallback() {
    this.playing = true;
    this.uiVideoPlay.emit({
      isInSlider: this.closestElement('ui-slider', this.el) ? true : false,
    });
    var event = new CustomEvent('videoPlay', {
      detail: {
        isInSlider: this.closestElement('ui-slider', this.el) ? true : false,
      },
    });
    window.dispatchEvent(event);
  }

  pauseCallback() {
    if (this.video?.readyState !== 1) {
      this.playing = false;
      this.uiVideoPause.emit({
        isInSlider: this.closestElement('ui-slider', this.el) ? true : false,
      });
      var event = new CustomEvent('videoPause', {
        detail: {
          isInSlider: this.closestElement('ui-slider', this.el) ? true : false,
        },
      });
      window.dispatchEvent(event);
    }
  }

  closestElement(selector, base) {
    function __closestFrom(el) {
      if (!el || el === document || el === window) return null;
      let found = el.closest(selector);
      return found ? found : __closestFrom(el.getRootNode().host);
    }

    return __closestFrom(base);
  }

  @Listen('message', { target: 'window' })
  onMessage(msg: MessageEvent<string>) {
    if (
      typeof msg.data === 'string' &&
      msg.data.startsWith(`VIDEO-${this.videoId}`)
    ) {
      if (msg.data === `VIDEO-${this.videoId}:PLAY`) {
        this.playingCallback();
      }

      if (msg.data === `VIDEO-${this.videoId}:PAUSE`) {
        this.pauseCallback();
      }
    }
  }

  @Watch('looped')
  componentDidLoad() {
    this.playerId = players[this.looped ? 'loop' : 'default'];
    this.htmlPath = window.ui.services.video.getVideoHTMLPath();
    // Warn user if they have video-id and src props with values
    this.src &&
      this.videoId &&
      console.warn(
        'You have "video-id" and "src" props with values. Should have only one'
      );
  }

  @Watch('src')
  onSrcChange() {
    this.displayError = false;
  }

  @Watch('videoContainer')
  watchVideoContainer() {
    if (this.contain && this.videoContainer && !this.observer) {
      this.videoContainer.style.width = '100%';
      this.observer = new ResizeObserver(() =>
        this.calculateAvailableSpaceAndSetPadding()
      );
      this.observer.observe(this.videoContainer);
    }
  }

  private calculateAvailableSpaceAndSetPadding() {
    // DEVNOTE: We set the aspectRatio only once – this will be optimized via "technical debt"-ticket
    if (!this.aspectRatio) {
      const aspectRatio =
        this.videoContainer.clientWidth / this.videoContainer.clientHeight;
      if (aspectRatio > 2 || aspectRatio < 1) return;
      this.aspectRatio = aspectRatio;
    }
    /*
     * DEVNOTE: As the video calculates it's height regarding to its width, we have to create paddings
     * If we would use the width, we couldn't use the resize observer anymore, that's why padding is the way to go.
     */
    if (this.el.clientHeight * this.aspectRatio < this.el.clientWidth) {
      this.el.style.padding = `0 ${(this.el.clientWidth - this.el.clientHeight * this.aspectRatio) / 2 - 1}px`;
    } else {
      this.el.style.padding = '0';
    }
  }

  /**
   * Play/Pause video and display/hide icon
   */
  playPause() {
    if (this.playing) {
      this.video.pause();
      this.pauseCallback();
    } else {
      this.video.play();
      this.playingCallback();
    }
  }

  @Listen('uiVideoIframeMessage', { target: 'window' })
  onUiVideoIframeListenerEvent(event: CustomEvent) {
    // Delegate all messages to the iframe
    this.iframe.postMessageIntoIframe(event.detail);
  }

  @Listen('uiSliderSlideChange', { target: 'window' })
  onUiSliderSlideChangeEvent(event: CustomEvent) {
    if (event.detail.videoIdsOfHiddenSlides.includes(this.videoId)) {
      /**
       * If the Video is part of a slider, it should pause when it's in an invisible ui-slider slide.
       * This is currently only implemented for MovingImage videos.
       */
      this.iframe.postMessageIntoIframe({ name: 'pauseVideo' });
    }
  }

  errorPlayVideo(error) {
    this.playing = false;
    this.displayError = true;
    this.uiVideoError.emit({ error });
  }

  render() {
    return (
      (this.src || (this.htmlPath && this.videoId && this.playerId)) && (
        <div
          class="ui-video"
          ref={(el) => {
            this.videoContainer = el as HTMLElement;
          }}
        >
          <figure class="ui-video__holder">
            {this.videoId && !this.src && (
              <ui-iframe
                ref={(el) => (this.iframe = el)}
                is-auto-resized
                is-lowest-element-activated
                allow-full-screen
                src={`${this.htmlPath}?video-id=${this.videoId}&player-id=${
                  this.playerId
                }${this.muted ? '&muted=true' : ''}`}
              ></ui-iframe>
            )}
            {this.src && !this.displayError && (
              <div class="ui-video__wrapper">
                <video
                  controls={this.playing}
                  ref={(el) => {
                    this.video = el as HTMLVideoElement;
                  }}
                  // DEVNOTE: quickfix to preload video-thumbnail in safari-mobile: https://muffinman.io/blog/hack-for-ios-safari-to-display-html-video-thumbnail/
                  src={`${this.src}#t=0.001`}
                  onPause={this.pauseCallback.bind(this)}
                  onPlay={this.playingCallback.bind(this)}
                  onError={this.errorPlayVideo.bind(this)}
                >
                  Browser not supported
                </video>
                {!this.playing && (
                  <div
                    class={{
                      'ui-video-overlay': true,
                      'ui-video-overlay--dark': this.overlay,
                    }}
                    onClick={this.playPause.bind(this)}
                  >
                    <div class="ui-video-overlay--icon">
                      <ui-icon icon="start" type="system" />
                    </div>
                  </div>
                )}
              </div>
            )}
            {this.displayError && (
              <div class="ui-video__error">
                <ui-icon
                  icon="info-circle"
                  type="system"
                  onClick={this.playPause.bind(this)}
                />
                <span
                  class="ui-video__error-text"
                  innerHTML={this.errorText}
                ></span>
              </div>
            )}
          </figure>
          <slot></slot>
          {this.copyrightText && (
            <div
              class="ui-video__copyright"
              innerHTML={`© ${this.copyrightText}`}
            ></div>
          )}
        </div>
      )
    );
  }
}
versionComponent(UiVideo);
